import type { ApiResponse, DynamicUser, DynamicSchema, DynamicCreateRequest, DynamicCreateResponse, DynamicUpdatePayload } from "../types/dynamic"

// Global API operation tracking
let apiOperationCallback: ((isActive: boolean) => void) | null = null

export const setApiOperationCallback = (callback: (isActive: boolean) => void) => {
  apiOperationCallback = callback
}

// Helper to wrap API calls with operation tracking
const withApiTracking = async <T>(operationName: string, apiCall: () => Promise<T>): Promise<T> => {
  try {
    console.log(`🌐 Iniciando operación API: ${operationName}`)
    apiOperationCallback?.(true)
    const result = await apiCall()
    console.log(`✅ Operación API completada: ${operationName}`)
    return result
  } catch (error) {
    console.error(`❌ Error en operación API ${operationName}:`, error)
    throw error
  } finally {
    apiOperationCallback?.(false)
  }
}

// =============================================================================
// DYNAMIC SYSTEM API FUNCTIONS
// =============================================================================

// VITE_API_URL llega del CI (vars.ENV_CONFIG del GitHub Environment) y gana; API_POR_DOMINIO queda como respaldo.
const API_POR_DOMINIO: Record<string, string> = {
  // Respaldo si el build no trae VITE_API_URL
  "staging.panel.femecot2026.integrameetings.com": "https://staging.panel2026.api.femecot2025.integrameetings.com",
}

const DYNAMIC_API_BASE_URL =
  import.meta.env.VITE_API_URL || import.meta.env.VITE_API_BASE_URL || API_POR_DOMINIO[window.location.hostname]

if (!DYNAMIC_API_BASE_URL) {
  console.error(`❌ Sin URL de API para ${window.location.hostname}: define VITE_API_URL o agrega el dominio en API_POR_DOMINIO`)
}

/**
 * Fetches dynamic users and schema from the new API endpoint
 */
export const fetchDynamicData = async (): Promise<ApiResponse> => {
  const MAX_RETRIES = 3
  const INITIAL_TIMEOUT = 30000 // 30 segundos
  let lastError: Error | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), INITIAL_TIMEOUT * (attempt + 1))

    try {
      console.log(`🌐 Obteniendo datos dinámicos del nuevo API (intento ${attempt + 1})...`)
      
      // GET simple: con barra final (sin ella Apache redirige con 301 a la carpeta) y sin Content-Type
      // (no hay cuerpo y obligaba al preflight). Una sola petición en vez de OPTIONS → 301 → OPTIONS → GET.
      const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/dashboard/`, {
        method: "GET",
        signal: controller.signal
      })

      clearTimeout(timeoutId)

      if (!response.ok) {
        if (response.status === 504 && attempt < MAX_RETRIES) {
          console.log(`⏳ Reintentando datos dinámicos (${attempt + 1}/${MAX_RETRIES}) en 5s...`)
          await new Promise(resolve => setTimeout(resolve, 5000 * (attempt + 1)))
          continue
        }
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data: ApiResponse = await response.json()
      
      // Validar la estructura de la respuesta
      if (!data.data || !data.data.schema || !Array.isArray(data.data.users)) {
        throw new Error("Estructura de respuesta inválida del API dinámico")
      }
    
      console.log(`✅ Datos dinámicos obtenidos: ${data.data.users.length} usuarios, ${Object.keys(data.data.schema).length} campos de schema`)
      return data
      
    } catch (error) {
      clearTimeout(timeoutId)
      lastError = error instanceof Error ? error : new Error(String(error))
      if (attempt < MAX_RETRIES) {
        const delay = 5000 * (attempt + 1) // Backoff exponencial
        console.log(`⏳ Reintentando datos dinámicos (${attempt + 1}/${MAX_RETRIES}) en ${delay/1000}s...`)
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
  }

  throw lastError || new Error('Failed to fetch dynamic data after retries')
}

/**
 * Updates a dynamic user via API
 */
export const updateDynamicUser = async (user: DynamicUser): Promise<void> => {
  return withApiTracking("updateDynamicUser", async () => {
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/users/${user.id}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(user),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (result.status !== 200) {
      throw new Error(result.message || "Error updating dynamic user")
    }
  })
}


/**
 * Creates one or multiple dynamic users using the specific API format
 */
export const createDynamicUser = async (createRequest: DynamicCreateRequest | DynamicCreateRequest[]): Promise<DynamicCreateResponse | DynamicUser[]> => {
  return withApiTracking("createDynamicUser", async () => {
    console.log("⚠️ Imprimiendo createRequest para depuración:", createRequest)
    const isArray = Array.isArray(createRequest)
    console.log(`🌐 Creando ${isArray ? 'múltiples usuarios' : 'usuario'} dinámico${isArray ? 's' : ''}:`, createRequest)
    
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/users/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(createRequest),
    })

    console.log("⚠️ Respuesta recibida del servidor:", response)

    if (!response.ok) {
      console.log("❌ Respuesta no OK del servidor:", response.status)
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()

    console.log("⚠️ Resultado parseado del servidor:", result)
    
    if (isArray) {
      // Para array, esperamos una respuesta con array de usuarios creados
      if (result.status !== 201 && result.status !== 200) {
        throw new Error(result.message || "Error creating dynamic users")
      }
      
      // La respuesta puede ser directamente un array o tener la estructura { data: users }
      const users = Array.isArray(result) ? result : (result.data || result.users || [])
      console.log(`✅ ${users.length} usuarios dinámicos creados exitosamente`)
      return users
    } else {
      // Para objeto único, mantenemos la respuesta original
      if (result.status !== 200) {
        throw new Error(result.message || "Error creating dynamic user")
      }
      
      console.log('✅ Usuario dinámico creado exitosamente:', result.data)
      console.log("⚠️ Respuesta completa del servidor:", result)
      return result
    }
  })
}

/**
 * Updates a dynamic user with only changed fields using unique field identifier
 */
export const updateDynamicUserByUniqueField = async (payload: DynamicUpdatePayload): Promise<void> => {
  return withApiTracking("updateDynamicUserByUniqueField", async () => {
    console.log('🌐 Actualizando usuario dinámico:', payload.uniqueFieldValue, payload.updateData)
    
    const response = await fetch(`${DYNAMIC_API_BASE_URL}/api/v1/users/${payload.uniqueFieldValue}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload.updateData),
    })

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const result = await response.json()
    if (result.status !== 200) {
      throw new Error(result.message || "Error updating dynamic user")
    }

    console.log('✅ Usuario dinámico actualizado exitosamente')
  })
}

/**
 * Legacy function wrapper for backward compatibility
 * This allows existing code to continue working while we transition
 */
export const fetchDynamicUsers = async (): Promise<{ users: DynamicUser[], schema: DynamicSchema }> => {
  const response = await fetchDynamicData()
  return {
    users: response.data.users,
    schema: response.data.schema
  }
}
